import { db } from "@/firebase";
import { MenuView, DailyAnalytics } from "@/model/entities";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from "firebase/firestore";

/**
 * Records a menu view event
 * This function is fire-and-forget to avoid blocking the UI
 */
export async function trackMenuView(
  restaurantId: string,
  visitorId: string,
  menuId?: string
): Promise<void> {
  try {
    const now = new Date();
    const dateString = now.toISOString().split("T")[0]; // YYYY-MM-DD

    const menuView: Omit<MenuView, "id"> = {
      restaurantId,
      visitorId,
      menuId,
      timestamp: now,
      date: dateString,
    };

    await addDoc(collection(db, "menuViews"), {
      ...menuView,
      timestamp: Timestamp.fromDate(now),
    });
  } catch (error) {
    // Log error but don't throw to avoid impacting user experience
    console.error("Failed to track menu view:", error);
  }
}

/**
 * Fetches menu views for a restaurant within a date range
 */
export async function fetchMenuViews(
  restaurantId: string,
  startDate: Date,
  endDate: Date
): Promise<MenuView[]> {
  try {
    const menuViewsRef = collection(db, "menuViews");
    const q = query(
      menuViewsRef,
      where("restaurantId", "==", restaurantId),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      where("timestamp", "<=", Timestamp.fromDate(endDate)),
      orderBy("timestamp", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        restaurantId: data.restaurantId,
        visitorId: data.visitorId,
        menuId: data.menuId,
        timestamp: data.timestamp.toDate(),
        date: data.date,
      } as MenuView;
    });
  } catch (error) {
    console.error("Failed to fetch menu views:", error);
    throw error;
  }
}

/**
 * Aggregates menu views by day
 * Returns daily analytics with total views and unique visitors
 */
export function aggregateViewsByDay(
  views: MenuView[],
  restaurantId: string
): DailyAnalytics[] {
  const dailyMap = new Map<string, { views: number; visitors: Set<string> }>();

  views.forEach((view) => {
    const dateKey = view.date || view.timestamp.toISOString().split("T")[0];

    if (!dailyMap.has(dateKey)) {
      dailyMap.set(dateKey, { views: 0, visitors: new Set() });
    }

    const dayData = dailyMap.get(dateKey)!;
    dayData.views++;
    dayData.visitors.add(view.visitorId);
  });

  const result: DailyAnalytics[] = [];
  dailyMap.forEach((data, date) => {
    result.push({
      date,
      restaurantId,
      totalViews: data.views,
      uniqueVisitors: data.visitors.size,
    });
  });

  return result.sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Fetches or computes daily analytics for a restaurant
 * For now, this computes on-the-fly. In production, you might use
 * a scheduled function to pre-aggregate data.
 */
export async function fetchDailyAnalytics(
  restaurantId: string,
  startDate: Date,
  endDate: Date
): Promise<DailyAnalytics[]> {
  const views = await fetchMenuViews(restaurantId, startDate, endDate);
  return aggregateViewsByDay(views, restaurantId);
}

/**
 * Fetches summary analytics for a date range
 */
export async function fetchAnalyticsSummary(
  restaurantId: string,
  startDate: Date,
  endDate: Date
): Promise<{ totalViews: number; totalUniqueVisitors: number }> {
  const views = await fetchMenuViews(restaurantId, startDate, endDate);
  const uniqueVisitors = new Set(views.map((v) => v.visitorId));

  return {
    totalViews: views.length,
    totalUniqueVisitors: uniqueVisitors.size,
  };
}
